import xss from "xss";

export const getPlainText = (wysiwygContent: string) => {
	return wysiwygContent.replace(/<[^>]*>/g, "");
};

export const getPlainTextLength = (wysiwygContent: string) => {
	return getPlainText(wysiwygContent).length;
};

// The editor emits `<p></p>` / `<p><br></p>` for a field the admin cleared, neither of which
// is blank to `trim()`.
export const isBlankHtml = (wysiwygContent: string) => getPlainText(wysiwygContent).trim() === "";

const styledTags = {
	b: [],
	i: [],
	em: [],
	strong: [],
	u: [],
};

export type SanitizeHtmlOptions = {
	onlyStyledText?: boolean;
	allowHeadings?: boolean;
	allowImages?: boolean;
	allowVariables?: boolean;
	allowTables?: boolean;
};
export const sanitizeHtml = (content: string, options: SanitizeHtmlOptions = {}) =>
	xss(content ?? "", {
		whiteList: options.onlyStyledText
			? styledTags
			: {
					a: ["href", "rel", "target", "class"],
					...styledTags,
					br: [],
					p: ["style"],
					ol: [],
					ul: [],
					li: [],
					span: options.allowVariables ? ["data-type"] : [],
					hr: [],
					...(options.allowHeadings
						? {
								h1: ["style"],
								h2: ["style"],
								h3: ["style"],
								h4: ["style"],
								h5: ["style"],
								h6: ["style"],
							}
						: {}),
					...(options.allowImages
						? {
								img: ["src", "alt", "style", "width", "height"],
							}
						: {}),
					...(options.allowTables
						? {
								table: ["style"],
								thead: [],
								tbody: [],
								tr: [],
								th: ["colspan", "rowspan", "style"],
								td: ["colspan", "rowspan", "style"],
								colgroup: [],
								col: ["style", "width"],
							}
						: {}),
				},
	});

// The wysiwyg editor renders paragraphs and lists with zero vertical margins (BaseWysiwyg's
// `[&_p]:mt-0 [&_p]:mb-0`), so emails must inline the same spacing or clients fall back to
// their default `margin: 1em 0` and every line break becomes a blank-line gap. Empty
// paragraphs (the editor's blank lines) need a `<br />` or they collapse to zero height.
// Run this on sanitizeHtml output, never on raw editor content.
export const applyWysiwygEmailSpacing = (sanitizedHtml: string): string =>
	sanitizedHtml
		.replace(/(<p(?:\s[^>]*)?>)\s*(<\/p>)/gi, "$1<br />$2")
		.replace(/<(p|ul|ol)((?:\s[^>]*)?)>/gi, (_match, tag: string, attrs: string) => {
			const styledAttrs = /style\s*=\s*"/i.test(attrs)
				? attrs.replace(/style\s*=\s*"/i, 'style="margin:0;')
				: `${attrs} style="margin:0"`;
			return `<${tag}${styledAttrs}>`;
		});

export const htmlToSms = (html: string): string => {
	// 1) Turn anchor tags into bare URLs
	let s = html.replace(/<a\b[^>]*href=["']([^"']+)["'][^>]*>[\s\S]*?<\/a>/gi, (_m, href: string) => ` ${href} `);

	// 2) Make obvious line-breaky tags into spaces (optional but nice for SMS)
	s = s.replace(/<(br|\/p|\/div|\/li)\s*\/?>/gi, " ");

	// 3) Drop remaining tags
	s = s.replace(/<[^>]+>/g, "");

	// 4) Decode entities (&nbsp;, &amp;, etc.)
	s = s
		.replace(/&nbsp;/gi, " ")
		.replace(/&amp;/gi, "&")
		.replace(/&lt;/gi, "<")
		.replace(/&gt;/gi, ">")
		.replace(/&quot;/gi, '"')
		.replace(/&#39;/g, "'");

	// 5) Normalize whitespace (convert NBSPs and collapse)
	s = s
		.replace(/\u00A0/g, " ")
		.replace(/\s+/g, " ")
		.trim();

	return s;
};

export const stripLinkNavigationFromHtml = (html: string): string =>
	html.replace(/<a\b([^>]*)>/gi, (_match, attrs: string) => {
		// 1) Drop navigation-related attributes so the anchor cannot open a URL
		const rest = attrs
			.replace(/\s+href\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
			.replace(/\s+target\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
			.replace(/\s+rel\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
			.trim();

		// 2) Rebuild the opening tag: keep other attrs (e.g. class), mark for display-only link CSS
		const spacer = rest.length > 0 ? " " : "";
		return `<a${spacer}${rest} data-link-display-only>`;
	});
